import axios from "axios";
import store from "@/store/index";
import { MessageBox } from "element-ui";
import { getToken, setToken } from "@/utils/storage";
import { message } from "@/utils/resetMessage";
import Vue from "vue";
import { setTempUser } from "./storage";

// 定义 Vue 实例 调用全局显示和关闭 loading 方法
const vm = new Vue();

// 跨境/CDN 回源链路下 10s 过短，正常请求也会被判超时后触发重试。
// 提高单次容忍度、同时把重试次数降到 1，
// 最坏耗时从 10*4+3*3=49s 降到 20+1+20=41s，且每次尝试的耐心翻倍。
export const REQUEST_TIMEOUT = 20000;

// Axios 默认配置
axios.defaults.baseURL = window.baseUrl;
axios.defaults.timeout = REQUEST_TIMEOUT;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.withCredentials = true;
axios.defaults.retry = 1; // 重试次数
axios.defaults.retryDelay = 1000; // 重试间隔

/**
 * 统一处理响应数据
 * @param {Object} res - 响应数据
 * @param {Function} resolve - Promise resolve
 * @param {Function} reject - Promise reject
 */
function handleResponse(res, resolve, reject) {
  if (res.code === constants.interfaceResult.success) {
    resolve(res.data);
  } else {
    // 处理401/403业务错误码，跳转到登录页
    if (res.code === 401 || res.code === 403) {
      handleUnauthorized();
      reject(res.msg);
      return;
    }
    if (getToken() && res.msg) {
      message.error({
        message: res.msg,
        showClose: true,
        duration: 4000,
        offset: 100,
      });
    }
    reject(res.msg);
  }
}

/**
 * 统一的 POST 请求封装
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {number} timeout - 超时时间
 * @param {Object} config - 额外配置（showTop, fileFlag 等）
 * @returns {Promise}
 */
function requestPost(url, params, timeout = REQUEST_TIMEOUT, config = {}) {
  return new Promise((resolve, reject) => {
    axios
      .post(url, params, { timeout, ...config })
      .then((res) => handleResponse(res, resolve, reject))
      .catch((err) => reject(err));
  });
}

/**
 * 对外接口 - 标准请求
 * @param {Object} options - 请求选项
 * @returns {Promise}
 */
export function request({ url, params, method = "post", timeout = REQUEST_TIMEOUT }) {
  if (method === "get") {
    return requestGet(url, params);
  }
  return requestPost(url, params, timeout);
}

/**
 * 对外接口 - 带 loading 的请求
 * @param {Object} options - 请求选项
 * @returns {Promise}
 */
export function requestNew({
  url,
  params,
  method = "post",
  timeout = REQUEST_TIMEOUT,
  isShow = "0",
}) {
  if (method === "get") {
    return requestGet(url, params);
  }
  return requestPost(url, params, timeout, { showTop: isShow });
}

/**
 * 对外接口 - 文件上传
 * @param {Object} options - 请求选项
 * @returns {Promise}
 */
export function requestFile({ url, formdata }) {
  return requestPost(url, formdata, 30000, {
    showTop: "1",
    fileFlag: "1",
  });
}

/**
 * 封装 get 方法
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @returns {Promise}
 */
function requestGet(url, params) {
  return new Promise((resolve, reject) => {
    axios
      .get(url, { params })
      .then((res) => handleResponse(res, resolve, reject))
      .catch((err) => reject(err.message));
  });
}

// Request 拦截器
axios.interceptors.request.use(
  (config) => {
    if (config.showTop === "1") {
      vm.$showLoading();
    }
    if (getToken()) {
      config.headers["Authorization"] = getToken();
    }
    config.headers["Content-Type"] =
      config.fileFlag === "1" ? "multipart/form-data" : "application/json";
    return config;
  },
  (error) => {
    vm.$hideLoading();
    return Promise.reject(error);
  }
);

/**
 * 处理 HTTP 错误状态码
 * @param {number} code - HTTP 状态码
 * @param {string} message - 错误信息
 */
function handleHttpError(code, message) {
  const errorMessages = {
    401: "登录过期，请重新登录",
    403: "登录状态已过期",
    503: "服务不可用，请稍后重试",
  };

  if (code >= 500) {
    return `错误信息：${message}，请截图返回给客服`;
  }
  return errorMessages[code] || message;
}

/**
 * 处理 401/403 未授权错误
 */
function handleUnauthorized() {
  localStorage.clear();
  window.globalRouter.push({ path: "/login" });
  message.error({
    message: "登录过期，请重新登录",
    duration: 5000,
    offset: 100,
    showClose: true,
  });
}

/**
 * 处理 403 需要确认的错误
 */
function handleForbidden() {
  MessageBox.confirm(
    "登录状态已过期，您可以继续留在该页面，或者重新登录",
    "系统提示",
    {
      confirmButtonText: "重新登录",
      cancelButtonText: "取消",
      type: "warning",
    }
  ).then(() => {
    handleUnauthorized();
  });
}

// Response 拦截器
axios.interceptors.response.use(
  (response) => {
    vm.$hideLoading();

    const res = response.data;

    // 请求成功
    if (response.status === 200 && res.code === 0) {
      return res;
    }

    // 处理业务错误码
    if (res.code === 403 || res.code === 401) {
      handleUnauthorized();
    } else if (res.code > 500) {
      message.error({
        message: `错误信息：${res.msg}，请截图返回给客服`,
        duration: 15000,
        offset: 100,
        showClose: true,
      });
    }

    return Promise.reject(
      new Error(res.msg || (res.error && res.error.message) || "未知错误")
    );
  },
  (error) => {
    vm.$hideLoading();

    const config = error.config;
    const response = error.response;

    // 有响应的情况（HTTP 错误）
    if (response) {
      const code = response.status;

      // 401直接跳转登录，403弹出确认框
      if (code === 401) {
        handleUnauthorized();
        return Promise.reject(error.message);
      }

      if (code === 403) {
        handleForbidden();
        return Promise.reject(error.message);
      }

      // 503 立即重试一次
      if (code === 503) {
        return new Promise((resolve) => {
          setTimeout(() => resolve(), 3000);
        }).then(() => axios(config));
      }

      // 其他服务器错误
      if (code >= 500) {
        message.error({
          message: handleHttpError(code, error.message),
          duration: 15000,
          offset: 100,
          showClose: true,
        });
        return Promise.reject(error);
      }
    }

    // 请求在建立前就失败时(如 DNS/连接被拒) error.config 可能不存在，
    // 此时无法重试，直接提示并 reject，避免读取 undefined 属性抛错。
    if (!config) {
      message.error({
        message: error.message,
        duration: 15000,
        offset: 100,
        showClose: true,
      });
      return Promise.reject(error.message);
    }

    // 请求重试逻辑
    config.__retryCount = config.__retryCount || 0;

    if (config.__retryCount >= axios.defaults.retry) {
      // 超过重试次数
      message.error({
        message: handleHttpError(response && response.status, error.message),
        duration: 15000,
        offset: 100,
        showClose: true,
      });
      return Promise.reject((response && response.data) || error);
    }

    config.__retryCount++;

    // 延迟重试
    return new Promise((resolve) => {
      setTimeout(() => resolve(), config.retryDelay || axios.defaults.retryDelay);
    }).then(() => axios(config));
  }
);
